import { AssessmentComponent } from "@pandas/assessment-engine";
import { FlowType } from "./flowTypeHelpers";
import { AllScreens, assessmentScreens, flowScreens, Screen } from "./steps";

const allConfigScreens: Array<AllScreens> = [
	"start",
	"what-you-need",
	"assessment-results",
	"user-info",
	"offer",
	"store-locations",
];

export const initializeSteps = (
	configScreens: Array<string>,
	configAssessmentScreens: Array<string>,
	flow: FlowType,
	finalizeOnVision: boolean,
	redirectStep: Screen | "",
	shouldKeepAssessmentResults: boolean,
	hasImei: boolean,
) => {
	const hasBiometricsOrAudio =
		configAssessmentScreens.includes("biometrics") || configAssessmentScreens.includes("audio");
	if (!hasBiometricsOrAudio && configScreens.includes("what-you-need"))
		configScreens = configScreens.filter((screen) => screen !== "what-you-need");
	let allScreens = flowScreens[flow];
	const configuredScreens = [...configScreens];
	const screensToRemove = allConfigScreens.filter((screen) => !configuredScreens.includes(screen));
	allScreens = allScreens.filter((screen) => !screensToRemove.includes(screen));
	if (!shouldKeepAssessmentResults) allScreens = allScreens.filter((screen) => screen !== "stored-assessment-results");
	if (hasImei) allScreens = allScreens.filter((screen) => screen !== "device-identified");
	allScreens = filterStepsAfterRedirection(allScreens, redirectStep);
	if (finalizeOnVision)
		allScreens = allScreens.map((screen) => {
			if (screen === "trade-in-completion" || screen === "trade-in-completion-from-home")
				return "trade-in-completion-on-vision";
			return screen;
		});
	const index = allScreens.indexOf("assessments");
	if (index !== -1) allScreens.splice(index, 1, ...(configAssessmentScreens as Array<AllScreens>));

	return allScreens;
};

export const filterStepsAfterRedirection = (
	screens: Array<AllScreens>,
	redirectStep: Screen | "",
): Array<AllScreens> => {
	if (redirectStep !== "") {
		const index = screens.indexOf(redirectStep);

		if (index !== -1) {
			const filteredScreens = screens.slice(0, index + 1);
			filteredScreens.push("redirection");

			return filteredScreens;
		}
	}
	return screens;
};

export interface Progress {
	currentStep: number;
	allSteps: number;
}

export class StepManager {
	private screens: Array<AllScreens>;
	private currentIndex: number;
	private visitedScreens: Set<AllScreens>;
	private _onFlowUpdate: (allScreens: Array<AllScreens>) => void;

	constructor(screens: Array<AllScreens>, onFlowUpdate: (allScreens: Array<AllScreens>) => void) {
		this.screens = screens;
		this.currentIndex = 0;
		this.visitedScreens = new Set();
		const firstScreen = screens[0];
		if (firstScreen) this.visitedScreens.add(firstScreen);
		this._onFlowUpdate = onFlowUpdate;
	}

	getFirstScreen(): AllScreens {
		return this.screens[0]!;
	}

	removeScreen(screen: AllScreens) {
		this.screens = this.screens.filter((s) => s !== screen);
		this._onFlowUpdate(this.screens);
	}

	getAndSetNextScreen(): AllScreens {
		this.currentIndex++;
		const nextScreen = this.screens[this.currentIndex]!;
		this.visitedScreens.add(nextScreen);
		return nextScreen;
	}

	getAndSetSecondOccurrenceOf(targetScreen: AllScreens): AllScreens | undefined {
		// 1. Find the next occurrence of `targetScreen` after current position
		const nextTargetIndex = this.screens.indexOf(targetScreen, this.currentIndex + 1);

		// 2. If found and has a next screen, return the screen after it
		if (nextTargetIndex !== -1 && nextTargetIndex + 1 < this.screens.length) {
			this.currentIndex = nextTargetIndex + 1;
			const resultScreen = this.screens[this.currentIndex];
			if (resultScreen) this.visitedScreens.add(resultScreen);
			return resultScreen;
		}
		return undefined;
	}

	getScreenAfter(screen: AllScreens): AllScreens {
		const index = this.screens.indexOf(screen);
		return this.screens[index + 1]!;
	}

	getAndSetPreviousScreen(): AllScreens {
		this.currentIndex--;
		const prevScreen = this.screens[this.currentIndex]!;
		this.visitedScreens.add(prevScreen);
		return prevScreen;
	}

	getCurrentScreen(): AllScreens {
		return this.screens[this.currentIndex]!;
	}

	setCurrentScreen(screen: AllScreens): void {
		const index = this.screens.indexOf(screen);
		this.currentIndex = index;
		this.visitedScreens.add(screen);
	}

	getProgress(): Progress {
		const progressScreens = this.screens.filter(
			(screen) => assessmentScreens.includes(screen as AssessmentComponent) || screen === "imei",
		);
		const currentScreen = this.screens[this.currentIndex];
		const currentProgressIndex = progressScreens.findIndex((screen) => screen === currentScreen);
		return { currentStep: currentProgressIndex + 1, allSteps: progressScreens.length };
	}

	getTripHistory(): Array<AllScreens> {
		return Array.from(this.visitedScreens);
	}
}
